import { jsx as _jsx } from "react/jsx-runtime";
import { memo, useContext, useMemo } from 'react';
import { useAction, useAppSelector } from 'shared/hooks/redux/hooks';
import { isEffectiveServiceViewSelector } from 'console/shared/contexts/device/state/is-service-view-selector';
import { TabContext } from 'console/navigation/context/type';
import { createSubsetSelector } from 'webui/utils/selectors';
import { staticTabDefs } from 'console/tabs/tabs-list';
import { getZ3KCustomTabs, noTabs } from 'console/tabs/custom';
import { select_ui_settings } from 'console/redux/settings-actions';
import { changeTab } from 'console/redux/main-actions';
import { LegacyDevice } from 'console/device/device';
import { DevicePermission } from 'console/device/device_ts';
import { is_dashboard_events_selected, is_dashboard_selected, } from 'console/dashboards/z3k/selectors';
import { selectZthDashboardActive } from 'console/dashboards/zth/actions';
import { selectMapDashboardActive } from 'pages/dashboard.map/model/selectors';
const select_custom_tab_device = createSubsetSelector([
    'id',
    'capabilities',
    'device_type',
    'z3k_config',
    'online',
    'hardware_type',
    'is_configured_by_zont_wizard',
    'last_connect_time',
]);
export const TabContextProvider = memo(function TabContextProvider(props) {
    const { ui, children, device, legacy_device } = props;
    const custom_tab_device = select_custom_tab_device(device);
    const dashboard_selected = useAppSelector(is_dashboard_selected) ?? false;
    const dashboard_events_selected = useAppSelector(is_dashboard_events_selected) ?? false;
    const zth_dashboard_selected = useAppSelector(selectZthDashboardActive);
    const map_selected = useAppSelector(selectMapDashboardActive) ?? false;
    // const _should_show_settings_button = useAppSelector(should_show_settings_button);
    const account = useAppSelector((state) => state.account);
    const isServiceView = useAppSelector(isEffectiveServiceViewSelector);
    const legacy_account = useMemo(() => ui.account, [ui.account]);
    const custom_tabs = useMemo(() => {
        if (custom_tab_device) {
            return getZ3KCustomTabs(custom_tab_device);
        }
        return noTabs;
    }, [custom_tab_device, noTabs]);
    const tabDefs = useMemo(() => [...custom_tabs, ...staticTabDefs], [custom_tabs, staticTabDefs]);
    const shown_tabs = useMemo(() => tabDefs.filter((def) => legacy_device != null &&
        device != null &&
        def.should_appear(legacy_device, legacy_account, device, account, isServiceView)), [legacy_device, device, legacy_account, account, isServiceView, tabDefs]);
    const selectedTabKey = useAppSelector((state) => select_ui_settings(state).selected_tab_key);
    const selectTabKey = useAction(changeTab);
    const selected_tab = useMemo(() => shown_tabs.find((def) => def.key == selectedTabKey) ?? shown_tabs[0], [shown_tabs, selectedTabKey]);
    const show_wizard_banner = useMemo(() => device == null
        ? false
        : device.device_type?.code != null &&
            LegacyDevice.has_access(account.id, device, DevicePermission.configure) &&
            device.is_configured_by_zont_wizard == 'need_configuration', [device, account.id]);
    const context = useMemo(() => ({
        device,
        custom_tab_device,
        dashboard_selected,
        dashboard_events_selected,
        shown_tabs,
        selected_tab,
        selectTabKey,
        legacy_device,
        show_wizard_banner,
        zth_dashboard_selected,
        map_selected,
        ui,
        account_id: account.id,
    }), [
        device,
        custom_tab_device,
        selected_tab,
        selectTabKey,
        dashboard_selected,
        dashboard_events_selected,
        zth_dashboard_selected,
        map_selected,
        shown_tabs,
        legacy_device,
        show_wizard_banner,
        ui,
        account.id,
    ]);
    return _jsx(TabContext.Provider, { value: context, children: children });
});
export const useTabContext = () => useContext(TabContext);
